import React, { useState, useMemo, useCallback } from 'react';
import type { PropertyAnalysisResult } from '../../types/calculations';
import { formatCurrency } from '../../utils/formatters';
import { Sliders } from 'lucide-react';
import type { DealType } from './DealTypeSelector';
import { useYardiComparison } from '../../hooks/useYardiComparison';
import { AssumptionsDrawer } from './AssumptionsDrawer';
import { useAssumptionDraftStore, type CapExItem, type BaselineValues } from '../../store/useAssumptionDraftStore';

// Core layout components
import { CollapsibleSection } from './CollapsibleSection';
import { FourScenarioMatrix } from './FourScenarioMatrix';
import { ProjectionChartsToggle } from './ProjectionChartsToggle';
import { DealSnapshotBar } from './DealSnapshotBar';
import { OfferPriceCards } from './OfferPriceCards';
import { OfferSpreadBar } from './OfferSpreadBar';
import { CumulativeReturnPanel } from './CumulativeReturnPanel';
import { UnitValidationSection } from './UnitValidationSection';
import { ScenarioSelector, labelToScenarioId, SCENARIO_OPTIONS } from './ScenarioSelector';
import type { ScenarioId } from './ScenarioSelector';
import { DataFoundationBar } from './DataFoundationBar';
import { SSOTAuditTrail } from './SSOTAuditTrail';

// Property components
import { PropertyHero } from './PropertyHero';
import { DataQualityCard } from './DataQualityCard';
import { RentAnalysisConsolidated } from './RentAnalysisConsolidated';
import { CapExBreakdown } from './CapExBreakdown';
import CapExFundingComparison from './CapExFundingComparison';
import { OpExBreakdown } from './OpExBreakdown';
import { TenYearTable } from './TenYearTable';
import { ExitAnalysis } from './ExitAnalysis';
import { EquityInOut } from './EquityInOut';
import { ReturnBreakdownCard } from './ReturnBreakdownCard';
import { FinancialWaterfallCard } from './FinancialWaterfallCard';
import { MarketIntelligence } from './MarketIntelligence';
import { MarketIntelligenceCard } from './MarketIntelligenceCard';

// Market & analysis components
import { TrajectoryChart } from './TrajectoryChart';
import { DealNarrativeCard } from './DealNarrativeCard';
import { ZandiDataCard } from './ZandiDataCard';
import { MarketDataSynthesis } from './MarketDataSynthesis';
import { MarketAssessment } from './MarketAssessment';

// NOI & rent components
import { ThreeStateNOICard } from './ThreeStateNOICard';
import { ThreeCapRatesCard } from './ThreeCapRatesCard';
import { SellerFinancingComparison } from './SellerFinancingComparison';
import { NegotiationCalculator } from './NegotiationCalculator';
import PlaybookSuggestionPanel from './PlaybookSuggestionPanel';
import { WholesalePanel } from './WholesalePanel';
import { MarketContextCard } from './MarketContextCard';
import { T12DataCard } from './T12DataCard';
import { RentEstimationSection } from './RentEstimationSection';
import { HoldPeriodSelector } from './HoldPeriodSelector';
import { SurgeIntelligenceCard } from './SurgeIntelligenceCard';
import { useActiveMarket } from '../../store/useMarketStore';

// Demographics (promoted from Tab2)
import { CensusDemographicPanel } from './CensusDemographicPanel';
import { DemographicInsightsCard } from './DemographicInsightsCard';
import { InvestmentIntelligenceCard } from './InvestmentIntelligenceCard';
import { RentUpliftEvidence } from './RentUpliftEvidence';

// Phase 191-195: Construction financing + renovation waterfall
// ConstructionFinancingCard removed — data merged into CapExFundingComparison paths
import { RenovationWaterfallCard } from './RenovationWaterfallCard';

// Phase 203: Wireframe parity components
import { ScenarioControlBar, type SourceMode, type FinancingMode, type TaxViewMode } from './ScenarioControlBar';
// Phase 212: 2D Exit Cap Sensitivity Matrix
import { ExitCapSensitivityMatrix } from './ExitCapSensitivityMatrix';
import { PriceScenarioGrid } from './PriceScenarioGrid';
import { CapExSystemTable } from './CapExSystemTable';
import { useScenarioToggle, useWhatIfScenario } from '../../hooks/useWhatIfScenario';

interface Tab1Props {
    data: PropertyAnalysisResult;
    dealType?: DealType;
}

const findCalcNode = (section: any, idPattern: string) =>
    section?.calculations?.find((c: any) =>
        c.id?.toLowerCase().includes(idPattern.toLowerCase()) ||
        c.calculation_id?.toLowerCase().includes(idPattern.toLowerCase()) ||
        c.label?.toLowerCase().includes(idPattern.toLowerCase())
    );

/**
 * Phase 212: Build a concise unit mix summary string for PropertyHero badge.
 * e.g. "2u: 2bd/1ba each" or "1x 1BR, 1x 2BR (Estimated)"
 */
function buildUnitMixSummary(unitMixAnalysis: any): string | undefined {
    if (!unitMixAnalysis) return undefined;
    const unitMix = unitMixAnalysis.unit_mix as Record<string, number> | undefined;
    if (!unitMix || Object.keys(unitMix).length === 0) return undefined;
    const entries = Object.entries(unitMix);
    if (entries.length === 1) {
        const [type, count] = entries[0];
        const bedLabel = type.replace('BR', 'bd').replace('Studio', 'Studio');
        return `${count}u: ${bedLabel}/1ba each (Est.)`;
    }
    return entries.map(([type, count]) => `${count}x ${type.replace('BR', 'bd')}`).join(', ') + ' (Est.)';
}


function buildBaseline(d: PropertyAnalysisResult): BaselineValues {
    const inp = d?.inputs ?? ({} as any);
    const rentTransp = d?.rent_capex_transparency;
    const capexData = d?.capex_analysis ?? ({} as any);

    // Extract capex items from breakdown_by_system
    const capexItems: CapExItem[] = Object.entries(capexData.breakdown_by_system || {}).map(
        ([name, cost], i) => ({
            id: `std-${i}`,
            name,
            costPerUnit: Math.round((cost as number) / (inp.total_units || 1)),
            isCustom: false,
        })
    );

    return {
        interestRate: d.projections_10yr?.interest_rate ?? 0.07,
        constructionLoanRate: 0.09,
        constructionLoanPoints: 0.01,
        constructionPeriodMonths: 12,
        rentPerUnit: rentTransp?.current_rent_per_unit || inp.rent_per_unit || 0,
        vacancyRate: inp.vacancy_rate ? (inp.vacancy_rate > 1 ? inp.vacancy_rate / 100 : inp.vacancy_rate) : 0.07,
        rentGrowth: d.projections_10yr?.rent_growth_rate ?? 0.03,
        expenseGrowthRate: 0.02,
        opexRate: rentTransp?.opex_rate || 0.425,
        capexItems,
        totalUnits: inp.total_units || 1,
    };
}

export const Tab1Financials: React.FC<Tab1Props> = ({ data, dealType = 'conventional' }) => {
    const market = useActiveMarket();
    const [returnsTab, setReturnsTab] = useState<'summary' | 'year-by-year' | 'exit'>('summary');
    const [activeData, setActiveData] = useState<PropertyAnalysisResult | null>(null);

    // Scenario control bar state (Phase 203)
    const [sourceMode, setSourceMode] = useState<SourceMode>(
        data?.deal_type === 'on-market' ? 'on-market' : 'off-market'
    );
    const [financingMode, setFinancingMode] = useState<FinancingMode>('conventional');
    const [taxViewMode, setTaxViewMode] = useState<TaxViewMode>('obbb-clean');
    const displayData = activeData || data;

    // CRH-01: CapEx preset state + recalculation
    const [activeCapexPreset, setActiveCapexPreset] = useState<'light' | 'full' | null>(null);
    const propertyId = String(data?.property_id || '');
    const capexPresetMutation = useWhatIfScenario(propertyId || undefined);

    const handleCapexPresetSelect = useCallback((capexValue: number) => {
        const capex = displayData?.capex_analysis as any;
        const roiDerived = capex?.total_budget > 0
            ? (displayData?.rent_capex_transparency as any)?.annual_noi_uplift
                ? (displayData?.rent_capex_transparency as any).annual_noi_uplift / 0.20
                : undefined
            : undefined;
        const totalBudget = capex?.total_budget || 0;
        const preset = roiDerived != null && Math.abs(capexValue - roiDerived) < Math.abs(capexValue - totalBudget)
            ? 'light'
            : 'full';
        setActiveCapexPreset(preset);
        capexPresetMutation.mutate(
            {
                overrides: { source: 'user', capex_total: capexValue } as any,
                hold_period_years: displayData?.inputs?.hold_period_years || 10,
            },
            {
                onSuccess: (response) => {
                    setActiveData(response.result as PropertyAnalysisResult);
                },
            }
        );
    }, [capexPresetMutation, displayData]);

    // Phase 205: ScenarioControlBar toggle → API re-fetch
    const scenarioToggle = useScenarioToggle(propertyId || undefined);

    // TaxViewMode values ARE the scenario keys (1:1 mapping)
    const taxScenarioKey = taxViewMode;

    // Trigger re-fetch when any toggle changes
    const handleToggleChange = useCallback((
        newSource: SourceMode,
        newFinancing: FinancingMode,
        newTaxKey: string,
    ) => {
        scenarioToggle.mutate(
            {
                mode: newSource,
                hold_period_years: data?.inputs?.hold_period_years || 10,
                tax_scenario: newTaxKey,
                financing_mode: newFinancing,
            },
            {
                onSuccess: (result) => {
                    setActiveData(result as PropertyAnalysisResult);
                },
            }
        );
    }, [scenarioToggle, data]);

    const handleSourceChange = useCallback((v: SourceMode) => {
        setSourceMode(v);
        handleToggleChange(v, financingMode, taxScenarioKey);
    }, [financingMode, taxScenarioKey, handleToggleChange]);

    const handleFinancingChange = useCallback((v: FinancingMode) => {
        setFinancingMode(v);
        handleToggleChange(sourceMode, v, taxScenarioKey);
    }, [sourceMode, taxScenarioKey, handleToggleChange]);

    const handleTaxViewChange = useCallback((v: TaxViewMode) => {
        setTaxViewMode(v);
        handleToggleChange(sourceMode, financingMode, v);
    }, [sourceMode, financingMode, handleToggleChange]);
    const openDrawer = useAssumptionDraftStore((s) => s.openDrawer);
    const changedFields = useAssumptionDraftStore((s) => s.getChangedFields());

    if (!data) {
        return <div className="p-4 text-stone-400">Loading financial data...</div>;
    }

    // Fetch Yardi comparison data
    const metro = market.display_name;
    const tier = ((data as any)?.ssot_params?.tier || 'Tier2') as 'Tier1' | 'Tier2' | 'Tier3';
    const { data: yardiData, isLoading: yardiLoading } = useYardiComparison({ metro, tier, enabled: !!data });

    const yardiComparison = yardiData ? {
        ssotRentGrowth: yardiData.rent_growth_comparison.ssot_value,
        yardiRentGrowth: yardiData.rent_growth_comparison.yardi_value,
        yardiForecast: yardiData.market_context.forecast_2026 ?? 0,
        yardiJobGrowth: yardiData.market_context.job_growth,
        yardiCompletions: yardiData.market_context.completions_pct,
        variance: yardiData.rent_growth_comparison.variance,
        assessment: yardiData.rent_growth_comparison.assessment as 'CONSERVATIVE' | 'ALIGNED' | 'AGGRESSIVE',
        source: yardiData.source,
    } : undefined;

    // Safe accessors — use displayData so recalculated results flow through
    const inputs = displayData?.inputs ?? ({} as any);
    const capex = displayData?.capex_analysis ?? {};
    const rentTransparency = displayData?.rent_capex_transparency;
    const opexBreakdown = displayData?.opex_breakdown;

    // ====================================================================
    // Global scenario selection (Phase 180)
    // ====================================================================
    const defaultScenario = useMemo<ScenarioId>(
        () => labelToScenarioId(displayData?.four_scenario_comparison?.best_scenario_label),
        [displayData?.four_scenario_comparison?.best_scenario_label],
    );
    const [selectedScenario, setSelectedScenario] = useState<ScenarioId>(defaultScenario);

    // Selected offer solution — driven by global scenario selector
    const selectedOffer: any = displayData?.offer_solutions?.find(
        (s: any) => s.scenario === selectedScenario,
    ) ?? displayData?.offer_solutions?.[0];
    const hasOfferYbY = !!(selectedOffer?.year_by_year?.length);

    // When offer solutions exist, use their equity/projections for Year-by-Year + Exit
    const equityRequired = hasOfferYbY
        ? (selectedOffer.equity_required || 1)
        : (displayData?.projections_10yr?.equity_required || 1);

    // Projection data — prefer offer solution year_by_year (same price basis as Summary)
    // Falls back to projections_10yr (asking price) when no offers available
    const projections: any[] | undefined = hasOfferYbY
        ? (selectedOffer as any).year_by_year
        : displayData?.projections_10yr?.projections;
    const proj5yr: any = projections?.find((p: any) => p.year === 5);
    const proj10yr: any = projections?.find((p: any) => p.year === 10);

    // Extract key metrics
    const ltv = hasOfferYbY ? ((selectedOffer as any).ltv ?? 0) : (findCalcNode(displayData?.capital_stack, 'ltv')?.value ?? 0);
    const debtService = hasOfferYbY
        ? ((selectedOffer as any).year_by_year?.[0]?.debt_service ?? 0)
        : (findCalcNode(displayData?.debt_service, 'annual')?.value ?? 0);

    // IRR — from offer solution when available
    const irr5yr = hasOfferYbY ? ((selectedOffer as any).irr_5yr ?? 0) : (proj5yr?.exit_irr ?? 0);

    // Cap rates — single source from API
    const exitCapRate = displayData?.projections_10yr?.exit_cap_rate ?? 0;
    // Entry cap = uplifted NOI / offer price — always the right metric for the investor
    const hasCapex = ((capex as any)?.total_budget ?? 0) > 0;
    const entryCapRate = hasCapex
        ? (displayData?.three_cap_rates?.yield_on_cost ?? displayData?.three_cap_rates?.entry_cap ?? 0)
        : (displayData?.three_cap_rates?.entry_cap ?? 0);

    // Stabilized NOI — single source from three-state model
    const stabilizedNoi = displayData?.three_state_noi?.stabilized_noi ?? 0;

    // Best-scenario IRR and EM — computed INDEPENDENTLY across all 4 solutions (Phase 180)
    const bestIrr = displayData?.offer_solutions?.reduce((max: number, sol: any) => {
        const irr = sol.irr_10yr ?? sol.irr_5yr ?? 0;
        return irr > max ? irr : max;
    }, 0) ?? null;

    const bestEm = displayData?.offer_solutions?.reduce((max: number, sol: any) => {
        const em = sol.equity_multiple_10yr ?? sol.equity_multiple_5yr ?? 0;
        return em > max ? em : max;
    }, 0) ?? null;

    // Wholesale mode
    if (dealType === 'wholesale') {
        return (
            <div className="space-y-6 bg-obsidian min-h-screen p-6">
                <PropertyHero
                    address={inputs.address || `Property #${inputs.property_id || 'Unknown'}`}
                    location={inputs.neighborhood || inputs.submarket || market.display_name}
                    tier={inputs.tier ? parseInt(inputs.tier.replace('Tier', '')) : 2}
                    units={inputs.total_units || 0}
                    bedrooms={inputs.unit_mix || undefined}
                    unitMix={buildUnitMixSummary(data?.unit_mix_analysis)}
                    latitude={inputs.latitude}
                    longitude={inputs.longitude}
                    suggestedOffer={undefined}
                    askingPrice={(data as any)?.deal?.asking_price || inputs.asking_price}
                    verdict={undefined}
                    dealType="Wholesale"
                    propertyId={data?.property_id || inputs.property_id}
                />
                <MarketContextCard zandiScoring={data?.zandi_scoring} zandiProjection={data?.zandi_adjusted_projection} />
                <WholesalePanel
                    askingPrice={inputs.purchase_price}
                    estimatedARV={inputs.market_value || inputs.zestimate}
                    propertyAddress={inputs.address}
                />
            </div>
        );
    }

    // ====================================================================
    // SSOT assumptions for drawer reference tab
    // ====================================================================
    const ssotAssumptions = {
        effectiveTaxRate: data.tax_analysis?.effective_tax_rate ?? 0,
        bonusDepreciationRate: 1.00,
        permanentLoanRate: data.projections_10yr?.interest_rate ?? 0,
        constructionLoanRate: 0.09,
        constructionLoanPoints: 0.01,
        constructionPeriodMonths: 12,
        tier1OpExRate: tier === 'Tier1' ? (rentTransparency?.opex_rate ?? 0) : 0.39,
        tier2OpExRate: tier === 'Tier2' ? (rentTransparency?.opex_rate ?? 0) : 0.425,
        tier3OpExRate: tier === 'Tier3' ? (rentTransparency?.opex_rate ?? 0) : 0.448,
        tier1CapExPerUnit: tier === 'Tier1' ? ((capex as any)?.ssot_per_unit ?? 0) : 35000,
        tier2CapExPerUnit: tier === 'Tier2' ? ((capex as any)?.ssot_per_unit ?? 0) : 28000,
        tier3CapExPerUnit: tier === 'Tier3' ? ((capex as any)?.ssot_per_unit ?? 0) : 22000,
        exitCapRule: 'Going-in cap - 100bp',
        rentGrowthRate: (data.projections_10yr?.rent_growth_rate ?? 0) / 100,
        noiGrowthRate: (data.projections_10yr?.rent_growth_rate ?? 0) / 100,
    };

    const ssotYardiForDrawer = yardiComparison ? {
        ssotRentGrowth: yardiComparison.ssotRentGrowth,
        yardiRentGrowth: yardiComparison.yardiRentGrowth,
        variance: yardiComparison.variance,
        assessment: yardiComparison.assessment,
        source: yardiComparison.source,
    } : undefined;

    // ====================================================================
    // Buy & Hold mode — "The Investor Decision Flow" layout (Phase 210)
    // ====================================================================
    return (
        <div className="space-y-6 bg-obsidian min-h-screen p-4 lg:p-6">

            {/* ================================================================
                ALWAYS VISIBLE: PropertyHero + DealSnapshotBar + Banner
               ================================================================ */}

            <PropertyHero
                address={inputs.address || `Property #${inputs.property_id || 'Unknown'}`}
                location={inputs.neighborhood || inputs.submarket || market.display_name}
                tier={inputs.tier ? parseInt(inputs.tier.replace('Tier', '')) : 2}
                units={inputs.total_units || 0}
                bedrooms={inputs.unit_mix || undefined}
                unitMix={buildUnitMixSummary(data?.unit_mix_analysis)}
                latitude={inputs.latitude}
                longitude={inputs.longitude}
                suggestedOffer={data?.suggested_offer_price}
                askingPrice={(data as any)?.deal?.asking_price || inputs.asking_price}
                verdict={
                    data?.deal_verdict?.verdict === 'GO' ? 'GO'
                    : data?.deal_verdict?.verdict === 'NO-GO' ? 'NOGO'
                    : data?.deal_verdict?.verdict === 'CONDITIONAL' ? 'NOGO'
                    : (irr5yr >= 0.15 ? 'GO' : 'NOGO')
                }
                dealType={data?.deal_type}
                propertyId={data?.property_id || inputs.property_id}
            />

            {/* DealSnapshotBar — enhanced with best IRR + EM */}
            <DealSnapshotBar
                units={inputs.total_units || 0}
                unitConfidence={data?.unit_confidence ?? 'LOW'}
                unitSource={data?.unit_source ?? 'Unknown'}
                capexBudget={(capex as any).total_budget || 0}
                stabilizedNoi={data?.three_state_noi?.uplifted_noi ?? stabilizedNoi}
                entryCapRate={entryCapRate}
                hasCapex={hasCapex}
                offerRange={data?.offer_range ? {
                    low: data.offer_range.low_offer ?? 0,
                    high: data.offer_range.max_offer ?? 0,
                } : undefined}
                bestIrr={bestIrr}
                bestEm={bestEm}
                verdict={
                    data?.deal_verdict?.verdict === 'GO' ? 'GO'
                    : data?.deal_verdict?.verdict === 'NO-GO' ? 'NOGO'
                    : data?.deal_verdict?.verdict === 'CONDITIONAL' ? 'CONDITIONAL'
                    : undefined
                }
                dataConfidence={data?.data_confidence}
                leadScore={(data as any).lead_score ?? null}
            />

            {/* Scenario Control Bar (Phase 203) — unified toggles matching wireframe */}
            <ScenarioControlBar
                source={sourceMode}
                financing={financingMode}
                taxView={taxViewMode}
                onSourceChange={handleSourceChange}
                onFinancingChange={handleFinancingChange}
                onTaxViewChange={handleTaxViewChange}
            />

            {/* Global Scenario Selector (Phase 180) — below snapshot, above banner */}
            {displayData?.offer_solutions && displayData.offer_solutions.length > 0 && (
                <ScenarioSelector
                    selected={selectedScenario}
                    onChange={setSelectedScenario}
                    bestScenario={defaultScenario}
                />
            )}

            {/* Modified Assumptions Banner */}
            {activeData && (
                <div className="bg-amber-900/20 border border-amber-600/40 rounded-lg px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sliders size={16} className="text-amber-400" />
                        <span className="text-amber-300 text-sm font-medium">Modified Assumptions Active</span>
                        <span className="text-amber-400 text-sm">
                            {activeCapexPreset
                                ? `(CapEx: ${activeCapexPreset === 'light' ? 'Light Refresh' : 'Full Renovation'})`
                                : changedFields.length > 0 ? `(${changedFields.length} changed)` : ''
                            }
                        </span>
                    </div>
                    <button
                        onClick={() => { setActiveData(null); setActiveCapexPreset(null); useAssumptionDraftStore.getState().resetAll(); }}
                        className="text-amber-400 hover:text-amber-300 text-sm border border-amber-600/40 px-2 py-1 rounded transition-colors"
                    >
                        Reset to Original
                    </button>
                </div>
            )}

            {/* ================================================================
                SECTION 1: WHAT CAN I PAY? (always visible)
               ================================================================ */}

            {displayData.four_scenario_comparison && (
                <>
                    <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-3 flex-1">
                            <h2 className="text-sm font-semibold tracking-widest text-gold uppercase">
                                What Can I Pay?
                            </h2>
                            <div className="flex-1 border-t border-gold/30" />
                        </div>
                        <button
                            onClick={() => openDrawer(buildBaseline(displayData))}
                            className="flex items-center gap-1.5 text-sm text-gold hover:text-gold-bright border border-gold/30 hover:border-gold px-3 py-1.5 rounded transition-colors ml-3"
                        >
                            <Sliders size={14} />
                            Assumptions
                        </button>
                    </div>

                    <OfferPriceCards
                        scenarios={displayData.four_scenario_comparison!.scenarios}
                        offerSolutions={displayData.offer_solutions}
                        zandiOfferSolutions={displayData.zandi_offer_solutions}
                        bestScenarioLabel={displayData.four_scenario_comparison!.best_scenario_label}
                        noi={stabilizedNoi}
                        equityRequired={equityRequired}
                        capexBudget={(capex as any).total_budget || 0}
                        selectedScenario={selectedScenario}
                        onScenarioSelect={(id) => setSelectedScenario(id as ScenarioId)}
                    />

                    {displayData.offer_solutions && displayData.offer_solutions.length >= 2 && (
                        <OfferSpreadBar offerSolutions={displayData.offer_solutions} />
                    )}
                </>
            )}

            {/* Price Scenario Comparison Grid (Phase 204) — backend solver-computed */}
            {displayData.price_scenarios && displayData.price_scenarios.length > 0 && (
                <PriceScenarioGrid
                    priceScenarios={displayData.price_scenarios}
                    offerPrice={selectedOffer?.offer_price || data?.suggested_offer_price || inputs.purchase_price || 0}
                    marketValue={inputs.market_value || inputs.purchase_price || 0}
                />
            )}

            {/* Off-Market Context Callout */}
            {data?.deal_type === 'off-market' && (
                <div className="bg-charcoal/50 border border-slate-dark rounded-lg px-4 py-3 text-sm text-stone-400">
                    <span className="text-gold font-semibold">Off-Market Deal</span>{' '}
                    — No asking price. Offers are solver-derived: the engine finds the maximum price
                    you can pay at each scenario (Standard/OBBB × Clean/1031) while hitting your target IRR.
                    OPA market value ({formatCurrency(inputs.market_value || inputs.purchase_price || 0)}) is used as the reference point.
                </div>
            )}

            {/* ================================================================
                SECTION 2: THE PROPERTY
               ================================================================ */}

            <div className="flex items-center gap-3 pt-2">
                <h2 className="text-sm font-semibold tracking-widest text-gold uppercase">
                    The Property
                </h2>
                <div className="flex-1 border-t border-gold/30" />
            </div>

            <UnitValidationSection
                totalUnits={inputs.total_units || 0}
                unitSource={data?.unit_source}
                unitConfidence={data?.unit_confidence}
                unitCountReconciliation={data?.unit_count_reconciliation}
                unitMixAnalysis={data?.unit_mix_analysis}
            />

            <div className="space-y-4">
                {inputs.t12_noi && inputs.t12_noi > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <T12DataCard
                            t12Noi={inputs.t12_noi}
                            purchasePrice={inputs.purchase_price}
                            stabilizedNoi={stabilizedNoi}
                        />
                        <RentAnalysisConsolidated
                            currentRentPerUnit={rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || 0}
                            marketRentPerUnit={rentTransparency?.market_rent_per_unit || 0}
                            rentSources={rentTransparency?.rent_sources}
                            rentUpliftPercent={
                                (rentTransparency as any)?.rent_uplift_percent ||
                                (rentTransparency?.current_rent_per_unit && rentTransparency?.market_rent_per_unit
                                    ? ((rentTransparency.market_rent_per_unit - rentTransparency.current_rent_per_unit) / rentTransparency.current_rent_per_unit) * 100 : 0)
                            }
                            annualRentUplift={rentTransparency?.annual_rent_uplift}
                            totalUnits={inputs.total_units || 1}
                            capexBudget={(capex as any).total_budget || 0}
                            rentValidation={data.rent_validation}
                            currentRentSource={rentTransparency?.current_rent_source}
                            marketRentSource={(rentTransparency as any)?.market_rent_source}
                            currentRentConfidence={rentTransparency?.current_rent_confidence}
                            rentDecision={data.rent_decision}
                        />
                    </div>
                ) : data.rent_decision && (data.rent_decision as any).sources?.length > 0 ? (
                    <RentAnalysisConsolidated
                        currentRentPerUnit={rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || 0}
                        marketRentPerUnit={rentTransparency?.market_rent_per_unit || 0}
                        rentSources={rentTransparency?.rent_sources}
                        rentUpliftPercent={
                            (rentTransparency as any)?.rent_uplift_percent ||
                            (rentTransparency?.current_rent_per_unit && rentTransparency?.market_rent_per_unit
                                ? ((rentTransparency.market_rent_per_unit - rentTransparency.current_rent_per_unit) / rentTransparency.current_rent_per_unit) * 100 : 0)
                        }
                        annualRentUplift={rentTransparency?.annual_rent_uplift}
                        totalUnits={inputs.total_units || 1}
                        capexBudget={(capex as any).total_budget || 0}
                        rentValidation={data.rent_validation}
                        currentRentSource={rentTransparency?.current_rent_source}
                        marketRentSource={(rentTransparency as any)?.market_rent_source}
                        currentRentConfidence={rentTransparency?.current_rent_confidence}
                        rentDecision={data.rent_decision}
                    />
                ) : (
                    <RentEstimationSection
                        rentSources={rentTransparency?.rent_sources || []}
                        blendedRent={rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || 0}
                        stabilizedRent={rentTransparency?.market_rent_per_unit || inputs.rent_per_unit || 0}
                        totalUnits={inputs.total_units || 1}
                        vacancyRate={inputs.vacancy_rate ? (inputs.vacancy_rate > 1 ? inputs.vacancy_rate / 100 : inputs.vacancy_rate) : 0.07}
                        opexRate={rentTransparency?.opex_rate || inputs.opex_rate_override || 0.425}
                        marketValue={inputs.market_value || inputs.purchase_price || 0}
                        confidence={rentTransparency?.current_rent_confidence || 'ESTIMATE'}
                        capexBudget={(capex as any).total_budget || 0}
                        apiNoiUplift={rentTransparency?.annual_noi_uplift}
                    />
                )}
            </div>

            <RentUpliftEvidence
                propertyId={displayData?.property_id ?? data.property_id}
                currentRentPerUnit={rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || undefined}
                marketRentPerUnit={rentTransparency?.market_rent_per_unit || undefined}
                annualRentUplift={rentTransparency?.annual_rent_uplift || undefined}
                capexBudget={(capex as any).total_budget || undefined}
                currentRentSource={rentTransparency?.current_rent_source || undefined}
                marketRentSource={(rentTransparency as any)?.market_rent_source || undefined}
                currentRentConfidence={rentTransparency?.current_rent_confidence || undefined}
            />

            {opexBreakdown ? (() => {
                const grossIncome = data?.three_state_noi?.uplifted_egi
                    ?? data?.three_state_noi?.stabilized_egi
                    ?? data?.three_state_noi?.stabilized_gpi
                    ?? 0;
                const ssotOpexRate = rentTransparency?.opex_rate ?? 0;
                return (
                    <OpExBreakdown
                        lineItems={(opexBreakdown.line_items || []).map((item: any) => ({
                            category: item.category, amount: item.amount,
                            formula: item.source === 'SSOT' ? `${((item.rate || 0) * 100).toFixed(1)}% of EGI` : 'Actual (OPA/City)',
                            ssotSource: item.source === 'SSOT' ? 'config.py' : undefined
                        }))}
                        totalOpEx={opexBreakdown.total_amount || 0}
                        grossIncome={grossIncome}
                        ssotTierRate={ssotOpexRate}
                    />
                );
            })() : <div className="text-sm text-stone-400 py-4">No operating expense data available</div>}

            {/* ================================================================
                SECTION 3: THE RENOVATION PLAN (CapEx + Construction + Waterfall)
               ================================================================ */}

            <div className="flex items-center gap-3 pt-2">
                <h2 className="text-sm font-semibold tracking-widest text-gold uppercase">
                    The Renovation Plan
                </h2>
                <div className="flex-1 border-t border-gold/30" />
            </div>

            {(capex as any)?.breakdown_by_system ? (() => {
                const currentRent = rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || 0;
                const marketRent = rentTransparency?.market_rent_per_unit || 0;
                const rentDelta = marketRent - currentRent;
                const unitCount = inputs.total_units || 1;
                const annualNoiUplift = rentTransparency?.annual_noi_uplift
                    ?? (rentTransparency?.annual_rent_uplift
                        ? rentTransparency.annual_rent_uplift * (1 - (rentTransparency.opex_rate ?? 0.425))
                        : 0);
                const totalBudget = (capex as any).total_budget || 0;
                const roiPct = annualNoiUplift > 0 && totalBudget > 0 ? annualNoiUplift / totalBudget : undefined;
                const payback = annualNoiUplift > 0 && totalBudget > 0 ? totalBudget / annualNoiUplift : undefined;
                const roiDerived = annualNoiUplift > 0 ? annualNoiUplift / 0.20 : undefined;
                return (
                    <CapExBreakdown
                        systems={Object.entries((capex as any).breakdown_by_system || {}).map(([name, cost]) => ({
                            name, description: `${name} renovation`, cost: cost as number, perUnit: false
                        }))}
                        totalUnits={unitCount}
                        totalCapEx={totalBudget}
                        sizingLogic={rentDelta > 0 ? {
                            rentUpliftPerUnit: rentDelta, totalUnits: unitCount,
                            annualNoiUplift, roiTarget: 0.20,
                        } : undefined}
                        roiPercent={roiPct}
                        paybackYears={payback}
                        roiDerivedBudget={roiDerived}
                        ssotPerUnit={(capex as any).ssot_per_unit}
                        capexFormulaWinner={(capex as any).capex_formula_winner || rentTransparency?.capex_formula_winner}
                        annualNoiUplift={annualNoiUplift}
                        onCapexPresetSelect={handleCapexPresetSelect}
                        activeCapexPreset={activeCapexPreset}
                    />
                );
            })() : <div className="text-sm text-stone-400 py-4">No CapEx data available</div>}

            {/* CapEx System Table — wireframe parity (Phase 203) */}
            {(capex as any)?.total_budget > 0 && (() => {
                const totalBudget = (capex as any).total_budget || 0;
                const unitCount = inputs.total_units || 1;
                const sqft = (inputs as any).total_sqft || (inputs as any).sqft || 0;
                const annualRentUplift = rentTransparency?.annual_rent_uplift || 0;
                return (
                    <CapExSystemTable
                        totalCapex={totalBudget}
                        totalUnits={unitCount}
                        totalSqft={sqft}
                        annualRentUplift={annualRentUplift}
                    />
                );
            })()}

            {/* Phase 214: CapEx Funding tracks the selected scenario's offer price */}
            {(() => {
                // Prefer per-scenario capex_funding from selectedOffer, fall back to top-level
                const scenarioFunding = (selectedOffer as any)?.capex_funding;
                const fundingData = scenarioFunding ?? displayData.capex_funding_comparison;
                if (!fundingData) return null;
                return (
                    <CapExFundingComparison
                        comparison={fundingData as any}
                        stabilizedNoi={stabilizedNoi}
                        annualDebtService={debtService}
                    />
                );
            })()}

            {/* Renovation Waterfall -- phased cash flow during renovation */}
            {(() => {
                if (!projections || projections.length === 0) return null;
                const capexBudget = (capex as any).total_budget || 0;
                if (capexBudget <= 0) return null;

                const totalUnits = inputs.total_units || 1;
                const currentRent = rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || 0;
                const marketRent = rentTransparency?.market_rent_per_unit || currentRent;

                const y1 = projections.find((p: any) => p.year === 1);
                const y2 = projections.find((p: any) => p.year === 2);
                const y3 = projections.find((p: any) => p.year === 3);
                if (!y1 || !y2 || !y3) return null;

                const phases = [
                    {
                        label: 'Year 1: Construction',
                        sublabel: 'Phase 1 Renovation',
                        noi: y1.noi || 0,
                        debtService: y1.debt_service || 0,
                        cashFlow: y1.pre_tax_cf ?? y1.cash_flow ?? ((y1.noi || 0) - (y1.debt_service || 0)),
                        occupancy: '87.5% (reno vacancy)',
                        rentPerUnit: currentRent,
                        isConstructionYear: true,
                    },
                    {
                        label: 'Year 2: Lease-Up',
                        sublabel: 'Full Occupancy',
                        noi: y2.noi || 0,
                        debtService: y2.debt_service || 0,
                        cashFlow: y2.pre_tax_cf ?? y2.cash_flow ?? ((y2.noi || 0) - (y2.debt_service || 0)),
                        occupancy: '93%',
                        rentPerUnit: marketRent,
                        isConstructionYear: false,
                    },
                    {
                        label: 'Year 3+: Stabilized',
                        sublabel: 'Stabilized Operations',
                        noi: y3.noi || 0,
                        debtService: y3.debt_service || 0,
                        cashFlow: y3.pre_tax_cf ?? y3.cash_flow ?? ((y3.noi || 0) - (y3.debt_service || 0)),
                        occupancy: '93%',
                        rentPerUnit: marketRent,
                        isConstructionYear: false,
                    },
                ];

                return (
                    <RenovationWaterfallCard
                        phases={phases}
                        totalUnits={totalUnits}
                        capexBudget={capexBudget}
                    />
                );
            })()}

            {/* ================================================================
                SECTION 4: YOUR RETURNS
               ================================================================ */}

            <div className="flex items-center gap-3 pt-2">
                <h2 className="text-sm font-semibold tracking-widest text-gold uppercase">
                    Your Returns
                </h2>
                <div className="flex-1 border-t border-gold/30" />
            </div>

            {/* NOI Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left: Three-State NOI */}
                {data.three_state_noi && (
                    <ThreeStateNOICard data={data.three_state_noi} />
                )}

                {/* Right: Financial Waterfall */}
                {(() => {
                    const threeState = displayData?.three_state_noi;
                    const noiState = threeState?.uplifted_noi ? 'Uplifted' : 'Stabilized';
                    const gpi = noiState === 'Uplifted'
                        ? (threeState?.uplifted_gpi ?? 0)
                        : (threeState?.stabilized_gpi ?? 0);
                    const egi = noiState === 'Uplifted'
                        ? (threeState?.uplifted_egi ?? 0)
                        : (threeState?.stabilized_egi ?? 0);
                    const opexAmt = noiState === 'Uplifted'
                        ? (threeState?.uplifted_opex ?? 0)
                        : (threeState?.stabilized_opex ?? 0);
                    const noiVal = noiState === 'Uplifted'
                        ? (threeState?.uplifted_noi ?? 0)
                        : (threeState?.stabilized_noi ?? stabilizedNoi);
                    const vacRate = inputs.vacancy_rate
                        ? (inputs.vacancy_rate > 1 ? inputs.vacancy_rate / 100 : inputs.vacancy_rate)
                        : 0.07;
                    const vacLoss = gpi - egi;
                    const opexRate = rentTransparency?.opex_rate ?? 0.425;

                    // Use scenario-specific values from the selected offer solution (Phase 180-02)
                    const waterfallDebtService = selectedOffer?.year_by_year?.[0]?.debt_service ?? debtService;
                    const waterfallEquity = selectedOffer?.equity_required ?? equityRequired;
                    const yr1TaxBenefit = selectedOffer?.year_by_year?.[0]?.tax_benefit
                        ?? displayData?.tax_analysis?.year_1_tax_benefit
                        ?? 0;

                    const preTaxCF = noiVal - waterfallDebtService;
                    const afterTaxCF = preTaxCF + yr1TaxBenefit;
                    const coc = waterfallEquity > 0 ? afterTaxCF / waterfallEquity : 0;

                    return (
                        <FinancialWaterfallCard
                            gpi={gpi}
                            vacancyRate={vacRate}
                            vacancyLoss={vacLoss}
                            egi={egi}
                            opexRate={opexRate}
                            opexAmount={opexAmt}
                            noi={noiVal}
                            annualDebtService={waterfallDebtService}
                            preTaxCashFlow={preTaxCF}
                            year1TaxBenefit={yr1TaxBenefit}
                            afterTaxCashFlow={afterTaxCF}
                            equityInvested={waterfallEquity}
                            cashOnCash={coc}
                            noiLabel={noiState}
                            scenarioLabel={SCENARIO_OPTIONS.find(o => o.id === selectedScenario)?.shortLabel}
                        />
                    );
                })()}
            </div>

            <div className="flex gap-1 bg-charcoal/40 p-1 rounded-lg border border-slate-dark">
                {([
                    { id: 'summary' as const, label: 'Summary' },
                    { id: 'year-by-year' as const, label: 'Year-by-Year' },
                    { id: 'exit' as const, label: 'Exit Scenarios' },
                ]).map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setReturnsTab(tab.id)}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                            returnsTab === tab.id
                                ? 'bg-gold/20 text-gold border border-gold/30'
                                : 'text-stone-400 hover:text-ivory hover:bg-charcoal/60'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab: Summary */}
            {returnsTab === 'summary' && displayData.offer_solutions && displayData.offer_solutions.length > 0 ? (
                <div className="space-y-4">
                    <CumulativeReturnPanel
                        offerSolutions={displayData.offer_solutions}
                        rentGrowthLabel={inputs.rent_growth ? `SSOT (${(inputs.rent_growth).toFixed(1)}%)` : undefined}
                        selectedScenario={selectedScenario}
                        onScenarioChange={setSelectedScenario}
                    />
                </div>
            ) : returnsTab === 'summary' && displayData.four_scenario_comparison ? (
                <div className="space-y-4">
                    <FourScenarioMatrix comparison={displayData.four_scenario_comparison} />
                </div>
            ) : null}

            {/* Tab: Year-by-Year */}
            {returnsTab === 'year-by-year' && (
                <div className="space-y-4">
                    <div className="text-sm text-stone-400 mb-2">
                        Showing: {SCENARIO_OPTIONS.find(o => o.id === selectedScenario)?.label ?? selectedScenario}
                    </div>
                    {dealType !== 'cash' && projections && projections.length > 0 && (
                        <TenYearTable
                            equityRequired={equityRequired}
                            projections={projections
                                .filter((p: any) => p.year > 0)
                                .map((p: any) => {
                                    const yearDebtService = p.debt_service || debtService || 0;
                                    const yearNoi = p.noi || 0;
                                    const preTaxCF = p.pre_tax_cf ?? p.cash_flow ?? (yearNoi - yearDebtService);
                                    const taxBenefit = p.tax_benefit || 0;
                                    const afterTaxCF = p.after_tax_cf ?? ((Number(preTaxCF) || 0) + (Number(taxBenefit) || 0));
                                    const afterTaxIrr = hasOfferYbY
                                        ? null
                                        : (p.year === 5 ? (data?.projections_10yr?.irr_5yr_after_tax ?? null) :
                                           p.year === 10 ? (data?.projections_10yr?.irr_10yr_after_tax ?? null) : null);
                                    return {
                                        year: p.year, noi: yearNoi, debtService: yearDebtService,
                                        preTaxCF: Number(preTaxCF) || 0, taxBenefit: Number(taxBenefit) || 0,
                                        afterTaxCF,
                                        afterTaxCoC: equityRequired > 0 ? afterTaxCF / equityRequired : 0,
                                        exitIrr: p.exit_irr ?? null, afterTaxIrr,
                                        cumulativeCF: p.cumulative_pre_tax ?? p.cumulative_cash_flow ?? null,
                                        afterTaxCumulativeCF: p.cumulative ?? p.after_tax_cumulative_cf ?? null,
                                        exitRecaptureTax: p.exit_recapture_tax ?? null,
                                        exitCapitalGainsTax: p.exit_capital_gains_tax ?? null,
                                        exitStateTax: p.exit_state_tax ?? null,
                                        exitTotalTaxes: p.exit_total_taxes ?? null,
                                        exitAfterTaxNetProceeds: p.exit_after_tax_net_proceeds ?? null,
                                        exitEquityMultiple: p.equity_multiple ?? p.exit_equity_multiple ?? null,
                                        loanBalance: p.loan_balance ?? null,
                                        salePrice: p.exit_sale_price ?? null,
                                    };
                                })}
                            zandiProjections={!hasOfferYbY ? data.zandi_adjusted_projection?.adjusted_projections
                                ?.filter((p: any) => p.year > 0) : undefined}
                        />
                    )}

                    {projections && projections.length > 0 && (
                        <ProjectionChartsToggle
                            projections={projections}
                            equityRequired={equityRequired}
                        />
                    )}
                </div>
            )}

            {/* Tab: Exit Scenarios */}
            {returnsTab === 'exit' && (
                <div className="space-y-4">
                    <div className="text-sm text-stone-400 mb-2">
                        Showing: {SCENARIO_OPTIONS.find(o => o.id === selectedScenario)?.label ?? selectedScenario}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {dealType !== 'cash' && projections && (
                            <ExitAnalysis
                                exitScenarios={[3, 5, 7, 10].map(year => {
                                    const proj = projections.find((p: any) => p.year === year);
                                    const propertyValue = proj?.exit_sale_price || proj?.exit_value || 0;
                                    const exitIrr = proj?.exit_irr ?? null;
                                    const irrPercent = exitIrr != null ? (exitIrr > 1 ? exitIrr : exitIrr * 100) : 0;
                                    const cumCF = proj?.cumulative ?? proj?.after_tax_cumulative_cf ?? proj?.cumulative_cash_flow ?? 0;
                                    const exitProceeds = proj?.exit_after_tax_net_proceeds ?? proj?.exit_net_proceeds ?? 0;
                                    return {
                                        year, propertyValue,
                                        remainingLoan: proj?.loan_balance || 0,
                                        equity: exitProceeds,
                                        totalReturn: cumCF + exitProceeds,
                                        irr: irrPercent
                                    };
                                })}
                                entryCapRate={entryCapRate}
                                exitCapRate={exitCapRate}
                            />
                        )}

                        {dealType !== 'cash' && equityRequired > 0 && proj5yr && proj10yr && (() => {
                            const y5SalePrice = proj5yr?.exit_sale_price || 0;
                            const y10SalePrice = proj10yr?.exit_sale_price || 0;
                            const sellingCostPct = data?.tax_analysis?.selling_cost_pct ?? 0.05;
                            const y5LoanPayoff = proj5yr?.loan_balance || 0;
                            const y10LoanPayoff = proj10yr?.loan_balance || 0;
                            const y5NetProceeds = proj5yr?.exit_after_tax_net_proceeds ?? proj5yr?.exit_net_proceeds ?? 0;
                            const y10NetProceeds = proj10yr?.exit_after_tax_net_proceeds ?? proj10yr?.exit_net_proceeds ?? 0;
                            const y5Irr = proj5yr?.exit_irr ?? 0;
                            const y10Irr = proj10yr?.exit_irr ?? 0;
                            const y5EqMult = proj5yr?.equity_multiple ?? proj5yr?.exit_equity_multiple ?? 0;
                            const y10EqMult = proj10yr?.equity_multiple ?? proj10yr?.exit_equity_multiple ?? 0;
                            const closingCostPct = data?.tax_analysis?.closing_cost_pct ?? 0.03;
                            const offerPrice = hasOfferYbY ? (selectedOffer as any).offer_price : inputs.purchase_price;
                            const closingCosts = offerPrice * closingCostPct;
                            const totalEquityInvested = equityRequired;
                            const constructionCarry = hasOfferYbY
                                ? ((selectedOffer as any).construction_carry_total ?? 0)
                                : (data?.projections_10yr?.construction_carry ?? 0);
                            const downPayment = totalEquityInvested - closingCosts - constructionCarry;
                            const operatingYears = projections?.filter((p: any) => p.year > 0) || [];
                            const y5TaxSavings = operatingYears.filter((p: any) => p.year <= 5).reduce((sum: number, p: any) => sum + (p.tax_benefit || 0), 0);
                            const y10TaxSavings = operatingYears.filter((p: any) => p.year <= 10).reduce((sum: number, p: any) => sum + (p.tax_benefit || 0), 0);
                            const y5AfterTaxEqMult = proj5yr?.exit_after_tax_equity_multiple ?? null;
                            const y10AfterTaxEqMult = proj10yr?.exit_after_tax_equity_multiple ?? null;
                            const y5AfterTaxIrr = hasOfferYbY ? ((selectedOffer as any).irr_5yr ?? null) : (data?.projections_10yr?.irr_5yr_after_tax ?? null);
                            const y10AfterTaxIrr = hasOfferYbY ? ((selectedOffer as any).irr_10yr ?? null) : (data?.projections_10yr?.irr_10yr_after_tax ?? null);
                            const y5AfterTaxCumCF = proj5yr?.cumulative ?? proj5yr?.after_tax_cumulative_cf ?? null;
                            const y10AfterTaxCumCF = proj10yr?.cumulative ?? proj10yr?.after_tax_cumulative_cf ?? null;
                            return (
                                <EquityInOut
                                    equityIn={{ downPayment, closingCosts, capEx: 0, constructionCarry, total: totalEquityInvested }}
                                    fiveYearExit={{
                                        saleProceeds: y5SalePrice, loanPayoff: y5LoanPayoff,
                                        sellingCosts: y5SalePrice * sellingCostPct, sellingCostPct, netEquity: y5NetProceeds,
                                        equityMultiple: y5EqMult, irr: y5Irr,
                                        cumulativeTaxSavings: y5TaxSavings,
                                        afterTaxEquityMultiple: y5AfterTaxEqMult ?? undefined,
                                        exitRecaptureTax: proj5yr?.exit_recapture_tax ?? undefined,
                                        exitCapitalGainsTax: proj5yr?.exit_capital_gains_tax ?? undefined,
                                        exitStateTax: proj5yr?.exit_state_tax ?? undefined,
                                        exitTotalTaxes: proj5yr?.exit_total_taxes ?? undefined,
                                        exitStrategy: (selectedOffer as any)?.exit_strategy ?? 'clean',
                                        afterTaxIrr: y5AfterTaxIrr ?? undefined,
                                        cumulativeAfterTaxCF: y5AfterTaxCumCF ?? undefined,
                                    }}
                                    tenYearExit={{
                                        saleProceeds: y10SalePrice, loanPayoff: y10LoanPayoff,
                                        sellingCosts: y10SalePrice * sellingCostPct, sellingCostPct, netEquity: y10NetProceeds,
                                        equityMultiple: y10EqMult, irr: y10Irr,
                                        cumulativeTaxSavings: y10TaxSavings,
                                        afterTaxEquityMultiple: y10AfterTaxEqMult ?? undefined,
                                        afterTaxIrr: y10AfterTaxIrr ?? undefined,
                                        cumulativeAfterTaxCF: y10AfterTaxCumCF ?? undefined,
                                        exitRecaptureTax: proj10yr?.exit_recapture_tax ?? undefined,
                                        exitCapitalGainsTax: proj10yr?.exit_capital_gains_tax ?? undefined,
                                        exitStateTax: proj10yr?.exit_state_tax ?? undefined,
                                        exitTotalTaxes: proj10yr?.exit_total_taxes ?? undefined,
                                        exitStrategy: (selectedOffer as any)?.exit_strategy ?? 'clean',
                                    }}
                                />
                            );
                        })()}

                        {(() => {
                            const projSrc = hasOfferYbY ? selectedOffer : data?.projections_10yr;
                            const rb5 = (projSrc as any)?.return_breakdown_5yr;
                            const rb10 = (projSrc as any)?.return_breakdown_10yr;
                            if (!rb5 || !rb10) return null;
                            return (
                                <ReturnBreakdownCard
                                    breakdown5yr={rb5}
                                    breakdown10yr={rb10}
                                    sellingCostPct={data?.tax_analysis?.selling_cost_pct}
                                />
                            );
                        })()}
                    </div>

                    <HoldPeriodSelector data={data} />

                    {data.zandi_adjusted_projection?.adjusted_projections && data.projections_10yr?.projections && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <TrajectoryChart
                                title="Rent Growth Trajectory"
                                subtitle="SSOT baseline vs Zandi momentum-adjusted NOI"
                                baselineData={data.projections_10yr.projections.filter(p => p.year > 0).map(p => p.noi)}
                                adjustedData={data.zandi_adjusted_projection.adjusted_projections.filter(p => p.year > 0).map(p => p.noi)}
                                confidenceHighData={data.zandi_adjusted_projection.confidence_high_noi}
                                confidenceLowData={data.zandi_adjusted_projection.confidence_low_noi}
                                formatValue={(v) => formatCurrency(v)}
                            />
                            <TrajectoryChart
                                title="Property Value Trajectory"
                                subtitle="SSOT baseline vs Zandi momentum-adjusted exit values"
                                baselineData={data.projections_10yr.projections.filter(p => p.year > 0).map(p => p.exit_sale_price || 0)}
                                adjustedData={data.zandi_adjusted_projection.adjusted_projections.filter(p => p.year > 0).map(p => p.exit_sale_price || 0)}
                                confidenceHighData={
                                    data.zandi_adjusted_projection.confidence_high_noi && exitCapRate > 0
                                        ? data.zandi_adjusted_projection.confidence_high_noi.map(noi => noi / exitCapRate)
                                        : undefined
                                }
                                confidenceLowData={
                                    data.zandi_adjusted_projection.confidence_low_noi && exitCapRate > 0
                                        ? data.zandi_adjusted_projection.confidence_low_noi.map(noi => noi / exitCapRate)
                                        : undefined
                                }
                                formatValue={(v) => formatCurrency(v)}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Cap Rate Detail — collapsed */}
            {data.three_cap_rates && (
                <CollapsibleSection title="Cap Rate Detail" defaultOpen={false}>
                    <ThreeCapRatesCard data={data.three_cap_rates} />
                </CollapsibleSection>
            )}

            {/* ================================================================
                SECTION 5: SENSITIVITY ANALYSIS (Phase 212)
               ================================================================ */}
            {displayData.exit_sensitivity_matrix && (
                <CollapsibleSection title="Sensitivity Analysis" defaultOpen={false}>
                    <ExitCapSensitivityMatrix
                        matrix={displayData.exit_sensitivity_matrix}
                        purchasePrice={inputs.purchase_price}
                    />
                </CollapsibleSection>
            )}

            {/* ================================================================
                SECTION 6: MARKET CONTEXT (collapsible, default collapsed)
               ================================================================ */}

            <CollapsibleSection title="Market Context" defaultOpen={false}>
                <div className="space-y-4">
                    {/* Zandi Market Intelligence */}
                    {data.zandi_scoring && (
                        <ZandiDataCard
                            zandiScoring={data.zandi_scoring}
                            zandiProjection={data.zandi_adjusted_projection as any}
                        />
                    )}

                    {data.deal_narrative && (
                        <DealNarrativeCard narrative={data.deal_narrative} />
                    )}

                    {/* Neighborhood & Demographics */}
                    <MarketIntelligence propertyId={String(data?.property_id || inputs.property_id || '')} />

                    {data.census_summary && (
                        <CensusDemographicPanel
                            census={data.census_summary}
                            marketName={market.display_name}
                        />
                    )}

                    <DemographicInsightsCard data={data} />

                    {/* Market Intelligence */}
                    <InvestmentIntelligenceCard data={data} />

                    {data.zandi_scoring && (
                        <MarketDataSynthesis data={data} yardiData={yardiData} />
                    )}

                    <SurgeIntelligenceCard submarketName={data.zandi_scoring?.name} />

                    <MarketAssessment
                        uplift={data.uplift_position}
                        riskReturns={data.risk_adjusted_returns}
                    />

                    {(yardiData || yardiLoading) && (
                        <MarketIntelligenceCard
                            yardiRentGrowth={yardiData?.rent_growth_comparison.yardi_value ?? 0}
                            marketContext={yardiData?.market_context ?? {}}
                            riskAssessment={yardiData?.risk_assessment}
                            source={yardiData?.source ?? ''}
                            isLoading={yardiLoading}
                        />
                    )}
                </div>
            </CollapsibleSection>

            {/* ================================================================
                SECTION 7: DATA APPENDIX (collapsible, default collapsed)
               ================================================================ */}

            <CollapsibleSection title="Data Appendix" defaultOpen={false}>
                <div className="space-y-4">
                    <DataFoundationBar
                        units={inputs.total_units || 0}
                        unitSource={data?.unit_source}
                        unitConfidence={data?.unit_confidence}
                        unitCountReconciliation={data?.unit_count_reconciliation}
                        rentPerUnit={rentTransparency?.current_rent_per_unit || inputs.rent_per_unit || 0}
                        rentSource={rentTransparency?.current_rent_source}
                        rentConfidence={rentTransparency?.current_rent_confidence}
                        rentDecision={data?.rent_decision}
                        opexRate={rentTransparency?.opex_rate || 0.425}
                        opexTier={inputs.tier || 'Tier2'}
                        opexBreakdown={opexBreakdown}
                        dataConfidence={data?.data_confidence}
                        sourcesChecked={data?.sources_checked}
                    />

                    <DataQualityCard
                        unitCountConfidence={(data.unit_confidence ?? 'LOW') as 'HIGH' | 'MEDIUM' | 'LOW'}
                        unitCountSource={data.unit_source ?? 'Unknown'}
                        unitCountValue={inputs.total_units || 0}
                        currentRentConfidence={rentTransparency?.current_rent_confidence as any || 'MEDIUM'}
                        currentRentSource={rentTransparency?.current_rent_source || 'Estimate'}
                        currentRentValue={formatCurrency(rentTransparency?.current_rent_per_unit || 0)}
                        marketRentConfidence="MEDIUM"
                        marketRentSource={(rentTransparency as any)?.market_rent_source || 'Zandi/RentCast'}
                        marketRentValue={formatCurrency(rentTransparency?.market_rent_per_unit || 0)}
                        capexBudgetConfidence="HIGH"
                        capexBudgetSource="ROI-Sized (20%)"
                        capexBudgetValue={formatCurrency((capex as any).total_budget || 0)}
                        unitCountReconciliation={data.unit_count_reconciliation}
                        propertyId={data?.property_id}
                    />

                    {data?.ssot_parameters && data.ssot_parameters.length > 0 && (
                        <SSOTAuditTrail
                            parameters={data.ssot_parameters}
                            calculatedAt={data.calculated_at}
                        />
                    )}
                </div>
            </CollapsibleSection>

            {/* ================================================================
                SECTION 8: SELLER FINANCING (conditional, unchanged)
               ================================================================ */}

            {dealType === 'seller_financing' && (
                <CollapsibleSection title="Seller Financing" defaultOpen={false}>
                    <div className="space-y-4">
                        {data?.financing_comparison && (
                            <SellerFinancingComparison
                                conventional={data.financing_comparison.conventional}
                                seller={data.financing_comparison.seller}
                                comparison={{
                                    monthly_payment_delta: data.financing_comparison.monthly_payment_delta,
                                    annual_debt_service_delta: data.financing_comparison.annual_debt_service_delta,
                                    equity_required_delta: data.financing_comparison.equity_required_delta,
                                    coc_delta: data.financing_comparison.coc_delta,
                                    break_even_rate: data.financing_comparison.break_even_rate,
                                }}
                                scenarios={data.financing_comparison.scenarios}
                                recommendedRate={data.financing_comparison.recommended_starting_rate}
                            />
                        )}
                        {inputs.purchase_price > 0 && (
                            <NegotiationCalculator
                                purchasePrice={inputs.purchase_price}
                                noi={stabilizedNoi}
                                bankRate={0.07}
                                bankDownPct={0.25}
                            />
                        )}
                        <PlaybookSuggestionPanel
                            financingType="seller_finance"
                            dealType={dealType}
                            propertyId={Number(data?.property_id || inputs.property_id) || undefined}
                            daysOnMarket={(data as any)?.deal?.days_on_market || (inputs as any).days_on_market}
                            priceReductionCount={(data as any)?.deal?.price_reduction_count}
                            isForeclosure={(data as any)?.deal?.is_foreclosure || (inputs as any).listing_status?.toLowerCase() === 'foreclosure'}
                            isVacant={inputs.occupied_units === 0 || !inputs.occupied_units}
                            hasLowEquity={ltv > 0.8}
                        />
                    </div>
                </CollapsibleSection>
            )}

            {/* ================================================================
                DRAWER: Merged Assumptions + SSOT Reference
               ================================================================ */}

            <AssumptionsDrawer
                data={displayData}
                propertyId={String(displayData?.property_id || inputs.property_id || '')}
                onRecalculated={(result) => setActiveData(result)}
                ssotAssumptions={ssotAssumptions}
                yardiComparison={ssotYardiForDrawer}
            />
        </div>
    );
};
